import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { consentApi } from "@/api/user";

const CONSENT_TYPE = "age_self_declared_16_plus";

/**
 * Blocks SSO users who never declared their age from using the app.
 *
 * Manual registrations enforce age >= 16 via a required date-of-birth on the
 * sign-up form (UserCreate.validate_age). SSO providers (Google, Apple) don't
 * always share birthday with us, so for SSO accounts we ask the user to
 * explicitly confirm they are 16+ on first sign-in, then store the confirmation
 * in the consent_records table (Art. 8 GDPR / § 8 BDSG).
 *
 * Rule: if the authenticated user has no birthday on file AND no
 * `age_self_declared_16_plus` consent on record, the modal blocks the UI
 * until they either accept or decline (decline = log out).
 */
export const SSOAgeGate = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const [needsCheck, setNeedsCheck] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      setNeedsCheck(false);
      return;
    }
    // Manual register users provided a birthday at sign-up and were age-verified
    // server-side; no need to re-ask them.
    if (user.birthday) {
      setNeedsCheck(false);
      return;
    }
    // SSO user: check whether they already confirmed age in the past.
    let cancelled = false;
    consentApi
      .getState(user.id)
      .then((state) => {
        if (cancelled) return;
        const entry = state.state?.[CONSENT_TYPE];
        // Need check if entry missing OR entry exists but was withdrawn (granted=false).
        setNeedsCheck(!entry || !entry.granted);
      })
      .catch(() => {
        // On error, fail-closed: show the modal. The user can always retry by
        // accepting; if the network is down the consent POST will also fail and
        // they can try again later.
        if (!cancelled) setNeedsCheck(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const content = {
    en: {
      title: "One quick confirmation",
      subtitle:
        "Before you continue, we need to confirm that you are old enough to use BuyForS.",
      body:
        "Because you signed in with Google or Apple, we don't have your date of birth on file. Under the GDPR (Art. 8) and German law (§ 8 BDSG), accounts on BuyForS must be held by someone aged 16 or older. Please confirm.",
      accept: "Yes, I am 16 or older",
      decline: "I am younger — sign me out",
      legalBasis:
        "Your confirmation is recorded with a timestamp under Art. 7(1) GDPR (proof of consent). You can withdraw at any time by deleting your account or emailing privacy@buyfors.com.",
      submitting: "Saving…",
    },
    de: {
      title: "Eine kurze Bestätigung",
      subtitle:
        "Bevor Sie fortfahren, müssen wir bestätigen, dass Sie alt genug sind, um BuyForS zu nutzen.",
      body:
        "Da Sie sich mit Google oder Apple angemeldet haben, liegt uns Ihr Geburtsdatum nicht vor. Nach Art. 8 DSGVO und § 8 BDSG müssen Konten auf BuyForS von Personen ab 16 Jahren gehalten werden. Bitte bestätigen Sie.",
      accept: "Ja, ich bin 16 oder älter",
      decline: "Ich bin jünger - bitte abmelden",
      legalBasis:
        "Ihre Bestätigung wird mit Zeitstempel gemäß Art. 7 Abs. 1 DSGVO (Einwilligungsnachweis) gespeichert. Sie können sie jederzeit widerrufen, indem Sie Ihr Konto löschen oder eine E-Mail an privacy@buyfors.com senden.",
      submitting: "Speichern…",
    },
  };

  const t = content[language as "en" | "de"] ?? content.en;

  const handleAccept = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await consentApi.record(user.id, {
        consent_type: CONSENT_TYPE,
        granted: true,
        method: "sso_post_signup_modal",
      });
      setNeedsCheck(false);
    } catch (err) {
      // Leave the modal open so the user can retry. The fail-closed behaviour
      // here is intentional: we'd rather block than let an under-16 through
      // because of a network blip.
      console.error("Failed to record age confirmation", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (user) {
      // Record the negative consent for our audit trail before logging the user out.
      try {
        await consentApi.record(user.id, {
          consent_type: CONSENT_TYPE,
          granted: false,
          method: "sso_post_signup_modal",
        });
      } catch (err) {
        console.warn("Failed to record under-16 declaration", err);
      }
    }
    await logout();
  };

  if (!needsCheck || !user) return null;

  return (
    <Dialog open={true} modal={true}>
      {/* No onOpenChange → cannot be dismissed by clicking outside or ESC */}
      <DialogContent
        className="sm:max-w-[500px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#9b87f5]" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-foreground">{t.body}</p>
          <p className="text-xs italic text-muted-foreground">{t.legalBasis}</p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleDecline}
            className="sm:mr-auto"
            disabled={submitting}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t.decline}
          </Button>
          <Button
            onClick={handleAccept}
            className="bg-gradient-to-r from-[#9b87f5] to-[#7E69AB] hover:opacity-90"
            disabled={submitting}
          >
            {submitting ? t.submitting : t.accept}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
